import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import type { UnconventionalConfigDetection } from '@/features/Quote/services/coverageRules';
import { isFeatureActive } from '@/lib/featureFlags';
import { addValueToOptions } from '@/services/utils';
import type { CoverageValues } from '@/types/app';
import { FeatureFlag } from '@/types/app';
import CoverageFormFields from './CoverageFormFields';
import {
	COVERAGE_OPTIONS,
	EMPLOYER_COST_OPTIONS,
	hasRealCurrentPolicy,
	normalizeCoverageValues,
	STANDARD_WAITING_PERIOD_VALUES
} from './constants';

interface CoverageProps {
	coverageValues: CoverageValues;
	onCoverageChange: (values: CoverageValues) => void;
	unconventionalDetection?: UnconventionalConfigDetection;
}

const Coverage = ({ coverageValues, onCoverageChange, unconventionalDetection }: CoverageProps) => {
	const { t } = useTranslation();
	const { config, appData } = useAppContext();
	const currentCoverage = useMemo(
		() => appData?.current_policy?.sick_leave_short_term?.coverage || {},
		[appData?.current_policy?.sick_leave_short_term?.coverage]
	);
	const hasCurrentPolicy = hasRealCurrentPolicy(currentCoverage);

	const canEditCoverage = isFeatureActive(config, FeatureFlag.ALLOW_USER_COVERAGE_OVERRIDE);

	const isUserChangeRef = useRef(false);

	const [initialCoverage, setInitialCoverage] = useState<CoverageValues>(() =>
		normalizeCoverageValues(currentCoverage)
	);

	const [localValues, setLocalValues] = useState<CoverageValues>(coverageValues);

	useEffect(() => {
		setLocalValues(coverageValues);
		if (!isUserChangeRef.current) {
			setInitialCoverage(
				hasCurrentPolicy ? normalizeCoverageValues(currentCoverage) : coverageValues
			);
		}
		isUserChangeRef.current = false;
	}, [coverageValues, currentCoverage, hasCurrentPolicy]);

	const handleChange = useCallback(
		(field: keyof CoverageValues, value: number) => {
			isUserChangeRef.current = true;
			const newValues = { ...localValues, [field]: value };
			setLocalValues(newValues);
			onCoverageChange(newValues);
		},
		[localValues, onCoverageChange]
	);

	const waitingPeriodOptions = useMemo(() => {
		let options = addValueToOptions(STANDARD_WAITING_PERIOD_VALUES, initialCoverage.waiting_period);
		options = addValueToOptions(options, Number(localValues.waiting_period));
		return options;
	}, [initialCoverage.waiting_period, localValues.waiting_period]);

	const employerCostOptions = useMemo(() => {
		let options = addValueToOptions(EMPLOYER_COST_OPTIONS, initialCoverage.employer_costs);
		options = addValueToOptions(options, Number(localValues.employer_costs));
		return options;
	}, [initialCoverage.employer_costs, localValues.employer_costs]);

	const periodOptions = useMemo(
		() => ({
			firstYearFirstHalf: addValueToOptions(
				addValueToOptions(
					COVERAGE_OPTIONS,
					initialCoverage.coverage_percentage_first_year_first_half
				),
				localValues.coverage_percentage_first_year_first_half
			),
			firstYearSecondHalf: addValueToOptions(
				addValueToOptions(
					COVERAGE_OPTIONS,
					initialCoverage.coverage_percentage_first_year_second_half
				),
				localValues.coverage_percentage_first_year_second_half
			),
			secondYearFirstHalf: addValueToOptions(
				addValueToOptions(
					COVERAGE_OPTIONS,
					initialCoverage.coverage_percentage_second_year_first_half
				),
				localValues.coverage_percentage_second_year_first_half
			),
			secondYearSecondHalf: addValueToOptions(
				addValueToOptions(
					COVERAGE_OPTIONS,
					initialCoverage.coverage_percentage_second_year_second_half
				),
				localValues.coverage_percentage_second_year_second_half
			)
		}),
		[initialCoverage, localValues]
	);

	const fieldErrors = useMemo(() => {
		const errors: Record<string, string> = {};
		if (!unconventionalDetection?.hasIssues) return errors;

		for (const rule of unconventionalDetection.triggeredRules) {
			switch (rule.type) {
				case 'excessive_waiting_days':
					errors.waiting_period = t('quotes.coverageWarning.excessiveWaiting90');
					break;
				case 'unusual_waiting_period': {
					const suggestedFix = rule.suggestFix(coverageValues);
					errors.waiting_period = t('quotes.coverageWarning.unusualWaitingPeriod', {
						days: suggestedFix.waiting_period
					});
					break;
				}
				case 'zero_first_half_only':
					errors.waiting_period = t('quotes.coverageWarning.zeroFirstHalf');
					break;
				case 'zero_first_year_both_halves':
					errors.waiting_period = t('quotes.coverageWarning.zeroFirstYear');
					break;
				case 'reverse_coverage_progression':
					errors.coverage_all = t('quotes.coverageWarning.reverseCoverage');
					break;
				case 'low_coverage':
					errors.coverage_all = t('quotes.coverageWarning.lowCoverage');
					break;
				case 'high_or_decimal_employer_costs':
					errors.employer_costs = t('quotes.coverageWarning.highOrDecimalEmployerCosts');
					break;
			}
		}
		return errors;
	}, [unconventionalDetection, t, coverageValues]);

	return (
		<div className="mt-3">
			<CoverageFormFields
				values={localValues}
				onChange={handleChange}
				fieldErrors={fieldErrors}
				disabled={!canEditCoverage}
				waitingPeriodOptions={waitingPeriodOptions}
				employerCostOptions={employerCostOptions}
				periodOptions={periodOptions}
				initialValues={hasCurrentPolicy ? initialCoverage : undefined}
				testIdPrefix="coverage-input"
				testIdAuxPrefix="coverage"
			/>
		</div>
	);
};

export default Coverage;
